// Reading frequency "vibe layer" — converts a user's transmission history
// into a normalized waveform vector that can be compared against others.

export const WAVEFORM_DAYS = 90;
export const WAVEFORM_RESOLUTION = 60; // SVG path samples

export interface TransmissionSignal {
  created_at: string;
  status?: string | null;
  rating?: number | null;
}

// Status carries intent. A "want-to-read" pings softly; "read" is the full beat.
const STATUS_WEIGHT: Record<string, number> = {
  'want-to-read': 0.35,
  'reading': 0.7,
  'read': 1.0,
};

const ratingBoost = (rating?: number | null): number => {
  if (!rating || rating < 1) return 0;
  // 1★ → 0.05, 5★ → 0.45 — never overwhelms the base signal
  return (Math.max(1, Math.min(5, rating)) - 1) * 0.1 + 0.05;
};

const signalWeight = (t: TransmissionSignal): number => {
  const base = STATUS_WEIGHT[t.status ?? 'read'] ?? 0.7;
  return base * (1 + ratingBoost(t.rating));
};

/**
 * Bucket transmissions into a daily intensity vector for the last
 * WAVEFORM_DAYS days. Index 0 = oldest day, last = today.
 */
export function buildWaveform(
  transmissions: TransmissionSignal[],
  now: Date = new Date()
): number[] {
  const buckets = new Array(WAVEFORM_DAYS).fill(0);
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const dayMs = 86400000;

  for (const t of transmissions) {
    const d = new Date(t.created_at);
    if (Number.isNaN(d.getTime())) continue;
    const dayUtc = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const daysAgo = Math.floor((todayUtc - dayUtc) / dayMs);
    if (daysAgo < 0 || daysAgo >= WAVEFORM_DAYS) continue;
    const idx = WAVEFORM_DAYS - 1 - daysAgo;
    buckets[idx] += signalWeight(t);
  }

  // Gentle 3-point smoothing so the waveform feels like a wave, not bars.
  const smoothed = buckets.map((_, i) => {
    const prev = buckets[i - 1] ?? buckets[i];
    const next = buckets[i + 1] ?? buckets[i];
    return (prev + buckets[i] * 2 + next) / 4;
  });

  return smoothed;
}

/**
 * Downsample a daily waveform to a smaller number of points for the SVG path.
 */
export function resampleWaveform(
  wave: number[],
  resolution: number = WAVEFORM_RESOLUTION
): number[] {
  if (wave.length === 0) return new Array(resolution).fill(0);
  if (wave.length === resolution) return wave.slice();
  const out = new Array(resolution).fill(0);
  const step = wave.length / resolution;
  for (let i = 0; i < resolution; i++) {
    const start = Math.floor(i * step);
    const end = Math.max(start + 1, Math.floor((i + 1) * step));
    let sum = 0;
    let count = 0;
    for (let j = start; j < end && j < wave.length; j++) {
      sum += wave[j];
      count++;
    }
    out[i] = count > 0 ? sum / count : 0;
  }
  return out;
}

/**
 * Cosine similarity between two waveforms. Returns 0–1 (clamped from -1..1
 * since reading signals are non-negative).
 */
export function resonance(a: number[], b: number[]): number {
  if (a.length !== b.length || a.length === 0) return 0;
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  if (na === 0 || nb === 0) return 0;
  return Math.max(0, Math.min(1, dot / (Math.sqrt(na) * Math.sqrt(nb))));
}

/**
 * Build an SVG path string for a waveform in a given viewBox.
 * Centers the wave vertically with a baseline; peaks rise from baseline.
 */
export function waveformPath(
  wave: number[],
  width: number,
  height: number,
  padding = 2
): string {
  if (wave.length === 0) return '';
  const max = Math.max(...wave, 0.0001);
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;
  const baseline = height - padding;
  const step = innerW / Math.max(1, wave.length - 1);

  const points = wave.map((v, i) => {
    const x = padding + i * step;
    const y = baseline - (v / max) * innerH;
    return [x, y] as const;
  });

  // Smooth cubic bezier through points
  let d = `M ${points[0][0].toFixed(2)} ${points[0][1].toFixed(2)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[i + 1];
    const cx = (x1 + x2) / 2;
    d += ` Q ${cx.toFixed(2)} ${y1.toFixed(2)} ${x2.toFixed(2)} ${y2.toFixed(2)}`;
  }
  return d;
}
